import { useQuery } from "@tanstack/react-query";
import { parseUTCDate } from "utils/date";

import { patientsApi } from "../apis";
import {
  PatientsPaginationRequestDtoGenderEnum,
  PatientsPaginationRequestDtoStatusEnum,
  PatientsPaginationRequestDtoSortByEnum,
  PatientsPaginationRequestDtoSortOrderEnum,
} from "../libs/cellera-sdk/models";
import { Patient } from "../types/patient";

export interface GetPatientsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  gender?: PatientsPaginationRequestDtoGenderEnum[];
  status?: PatientsPaginationRequestDtoStatusEnum[];
  birthDateFrom?: string;
  birthDateTo?: string;
  sortBy?: PatientsPaginationRequestDtoSortByEnum;
  sortOrder?: PatientsPaginationRequestDtoSortOrderEnum;
}

export interface UsePatientsResult {
  patients: Patient[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const usePatients = (params: GetPatientsParams): UsePatientsResult => {
  const queryKey = [
    "patients",
    params.page,
    params.pageSize,
    params.search,
    params.gender?.join(","),
    params.status?.join(","),
    params.birthDateFrom,
    params.birthDateTo,
    params.sortBy,
    params.sortOrder,
  ];

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const result = await patientsApi.getAllPatientsNextPageToken({
        patientsPaginationRequestDto: {
          pageSize: params.pageSize || 10,
          page: (params.page || 0) + 1, // API uses 1-based, UI uses 0-based
          search: params.search,
          gender:
            params.gender && params.gender.length > 0
              ? params.gender
              : undefined,
          status:
            params.status && params.status.length > 0
              ? params.status
              : undefined,
          birthDateFrom: params.birthDateFrom
            ? parseUTCDate(params.birthDateFrom)
            : undefined,
          birthDateTo: params.birthDateTo
            ? parseUTCDate(params.birthDateTo)
            : undefined,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
        },
      });

      return {
        patients: result.patients as Patient[],
        totalCount: result.pagination?.total ?? result.patients?.length ?? 0,
      };
    },
    staleTime: 30000, // Data considered fresh for 30s
    gcTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  return {
    patients: data?.patients ?? [],
    totalCount: data?.totalCount ?? 0,
    loading: isLoading,
    error: error ? (error as Error).message : null,
    refetch: () => {
      refetch();
    },
  };
};

export default usePatients;
